import { useState } from 'react';
import { X, Search, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface Friend {
  id: string;
  name: string;
  username: string;
  avatar?: string;
  kudoz: number;
  isFollowing?: boolean;
}

interface ShareReviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reviewId: string;
  restaurantName: string;
}

const mockFriends: Friend[] = [
  { id: '1', name: 'Alex Chen', username: '@alexchen', avatar: '', kudoz: 1520, isFollowing: true },
  { id: '2', name: 'Maria Garcia', username: '@mariaG', avatar: '', kudoz: 2340, isFollowing: true },
  { id: '3', name: 'David Kim', username: '@davidk', avatar: '', kudoz: 980, isFollowing: false },
  { id: '4', name: 'Sarah Wilson', username: '@sarahw', avatar: '', kudoz: 1890, isFollowing: true },
  { id: '5', name: 'James Lee', username: '@jameslee', avatar: '', kudoz: 1150, isFollowing: true },
  { id: '6', name: 'Emma Rodriguez', username: '@emmar', avatar: '', kudoz: 2100, isFollowing: true },
];

export default function ShareReviewModal({ open, onOpenChange, reviewId, restaurantName }: ShareReviewModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFriends, setSelectedFriends] = useState<string[]>([]);

  const filteredFriends = mockFriends.filter(friend =>
    friend.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    friend.username.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleFriend = (friendId: string) => {
    setSelectedFriends(prev =>
      prev.includes(friendId)
        ? prev.filter(id => id !== friendId)
        : [...prev, friendId]
    );
  };

  const handleShare = () => {
    console.log('Sharing review', reviewId, 'with friends:', selectedFriends);
    onOpenChange(false);
    setSelectedFriends([]);
    setSearchQuery('');
  };

  const handleCancel = () => {
    onOpenChange(false);
    setSelectedFriends([]);
    setSearchQuery('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="dialog-share-review">
        <DialogHeader>
          <DialogTitle>Share Review</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Share your review of <span className="font-medium text-foreground">{restaurantName}</span> with friends
          </p>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search friends..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="input-search-friends"
            />
          </div>

          {/* Selected Count */}
          {selectedFriends.length > 0 && (
            <div className="flex items-center gap-2">
              <Badge variant="secondary" data-testid="badge-selected-count">
                {selectedFriends.length} selected
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedFriends([])}
                data-testid="button-clear-selection"
              >
                Clear
              </Button>
            </div>
          )}

          {/* Friends List */}
          <div className="max-h-96 overflow-y-auto space-y-2">
            {filteredFriends.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-8" data-testid="text-no-friends">
                No friends found
              </p>
            ) : (
              filteredFriends.map((friend) => {
                const isSelected = selectedFriends.includes(friend.id);
                return (
                  <button
                    key={friend.id}
                    onClick={() => toggleFriend(friend.id)}
                    className={cn(
                      "w-full flex items-center gap-3 p-3 rounded-md border hover-elevate active-elevate-2 text-left transition-colors",
                      isSelected && "bg-primary/5 border-primary"
                    )}
                    data-testid={`friend-${friend.id}`}
                  >
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={friend.avatar} />
                      <AvatarFallback>{friend.name.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{friend.name}</div>
                      <div className="text-sm text-muted-foreground truncate">
                        {friend.username}
                      </div>
                    </div>
                    <div
                      className={cn(
                        "w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0",
                        isSelected
                          ? "bg-primary border-primary"
                          : "border-muted-foreground/30"
                      )}
                    >
                      {isSelected && <Check className="w-3 h-3 text-primary-foreground" />}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={handleCancel}
              className="flex-1"
              data-testid="button-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleShare}
              disabled={selectedFriends.length === 0}
              className="flex-1"
              data-testid="button-share-confirm"
            >
              Share {selectedFriends.length > 0 && `(${selectedFriends.length})`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
